import { useCallback, useEffect, useRef } from 'react';

import type { FieldValues, FormState } from 'react-hook-form';
import type IUseScrollToErrorOptions from './interfaces/IUseScrollToErrorOptions';

const SCROLL_TO_ERROR_DELAY_MS = 150;

type TFormStateErrors = FormState<FieldValues>['errors'];

type TScrollTargetOptions = Pick<
IUseScrollToErrorOptions, 'scrollContainer' | 'scrollContainerRef' | 'scrollContainerSelector'
>;

// Shared so it can be triggered either passively (FE/resolver validation errors, which land in
// formState together with submitCount in a single update) or imperatively right after an async
// BE error is mapped via setError (whose formState update can land in a separate, later render
// than the one that already bumped submitCount, so the passive effect below cannot be relied on).
const scrollToFirstErrorField = (
  errors: TFormStateErrors,
  { scrollContainer, scrollContainerRef, scrollContainerSelector }: TScrollTargetOptions,
) => {
  if (Object.keys(errors).length === 0) {
    return;
  }

  setTimeout(() => {
    const resolvedScrollContainer = (scrollContainerRef?.current && scrollContainerSelector)
      ? (scrollContainerRef.current.querySelector(scrollContainerSelector) as HTMLElement | null)
      : scrollContainer;
    const container = resolvedScrollContainer || document;

    const findFieldElement = (fieldPath: string): HTMLElement | null => {
      const trySelectors = (path: string): HTMLElement | null => {
        let el = container.querySelector(`[name="${path}"]`) as HTMLElement;
        if (el) return el;
        el = container.querySelector(`[id*="${path}"]`) as HTMLElement;
        if (el) return el;
        const lastPart = path.split('.').pop() ?? path;
        el = container.querySelector(`[name*="${lastPart}"]`) as HTMLElement;
        if (el) return el;
        return container.querySelector(`[id*="${lastPart}"]`) as HTMLElement;
      };
      const element = trySelectors(fieldPath);
      if (element) return element;
      const pathWithDots = fieldPath.replace(/_/g, '.');
      return pathWithDots !== fieldPath ? trySelectors(pathWithDots) : null;
    };

    const collectErrorFields = (errorsObj: typeof errors, prefix = ''): string[] => {
      const fields: string[] = [];
      Object.keys(errorsObj).forEach((key) => {
        const fullPath = prefix ? `${prefix}.${key}` : key;
        const error = errorsObj[key as keyof typeof errorsObj];
        if (error && typeof error === 'object') {
          if ('message' in error) {
            fields.push(fullPath);
          } else if (Array.isArray(error)) {
            error.forEach((item, index) => {
              if (item && typeof item === 'object') {
                fields.push(...collectErrorFields(item as typeof errors, `${fullPath}.${index}`));
              }
            });
          } else {
            fields.push(...collectErrorFields(error as typeof errors, fullPath));
          }
        }
      });
      return fields;
    };

    const errorFieldPaths = collectErrorFields(errors);
    const allElements: Array<{ element: HTMLElement, position: number }> = [];

    errorFieldPaths.forEach((fieldPath) => {
      const fieldElement = findFieldElement(fieldPath);
      if (fieldElement) {
        const fieldContainer = fieldElement.closest('[class*="root"]') || fieldElement;
        const element = fieldContainer as HTMLElement;
        const rect = element.getBoundingClientRect();

        let position: number;
        if (resolvedScrollContainer) {
          const containerRect = resolvedScrollContainer.getBoundingClientRect();
          position = rect.top - containerRect.top + resolvedScrollContainer.scrollTop;
        } else {
          position = rect.top + window.scrollY;
        }

        allElements.push({ element, position });
      }
    });

    if (allElements.length > 0) {
      allElements.sort((a, b) => a.position - b.position);
      const firstErrorElement = allElements[0].element;

      firstErrorElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, SCROLL_TO_ERROR_DELAY_MS);
};

const useScrollToError = ({
  formState,
  scrollContainer,
  scrollContainerRef,
  scrollContainerSelector,
}: IUseScrollToErrorOptions) => {
  // formState is a per-render proxy over a snapshot, so a closed-over formState.errors goes stale.
  // handleSubmit swaps in a fresh errors object on every resolver run and setError then mutates
  // that object in place, so only the latest render's reference tracks what setError writes.
  const errorsRef = useRef(formState.errors);
  errorsRef.current = formState.errors;

  useEffect(() => {
    const { errors, submitCount } = formState;

    if (submitCount > 0) {
      scrollToFirstErrorField(errors, { scrollContainer, scrollContainerRef, scrollContainerSelector });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.submitCount, formState.errors, scrollContainer,
    scrollContainerRef, scrollContainerSelector]);

  // Call this right after setError()/setFieldsErrors() in an async (BE) error handler. The
  // submitCount bump renders long before the response lands, so by then errorsRef.current holds
  // the same object setError mutates — the passive effect above cannot see that in-place write.
  const scrollToErrors = useCallback(() => {
    scrollToFirstErrorField(errorsRef.current, { scrollContainer, scrollContainerRef, scrollContainerSelector });
  }, [scrollContainer, scrollContainerRef, scrollContainerSelector]);

  return { scrollToErrors };
};

export default useScrollToError;
